import * as m from '@staiter/i18n';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { Breadcrumbs, PageHeader } from '~/components/data';
import { CompanyContextBanner } from '~/components/domain/CompanyContextBanner';
import { useToast } from '~/components/feedback';
import { Button, DatePicker, Textarea } from '~/components/primitives';
import { renderApiErrorMessage } from '~/lib/api/error-renderer';
import { type InvoiceUpdateBody, fetchInvoice, updateInvoice } from '~/lib/api/invoices';
import shared from './_auth._shared.module.css';

/**
 * EFE-6 — Invoice edit (header-only: dates + notes). Lines edit deferred —
 * лучше через credit-note + correction flow.
 * State guard: server returns 409 invoice.not_editable если state ≠ draft.
 */
export const Route = createFileRoute('/_auth/invoices/$id/edit')({
  component: InvoiceEdit,
});

function InvoiceEdit(): JSX.Element {
  const { id } = Route.useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const qc = useQueryClient();

  const query = useQuery({
    queryKey: ['invoices', id],
    queryFn: () => fetchInvoice(id),
  });

  const [form, setForm] = useState({
    issue_date: '',
    due_date: '',
    notes_ru: '',
  });
  const [primed, setPrimed] = useState(false);

  useEffect(() => {
    if (query.data && !primed) {
      const inv = query.data;
      setForm({
        issue_date: inv.issue_date ?? '',
        due_date: inv.due_date ?? '',
        notes_ru: inv.notes_ru ?? '',
      });
      setPrimed(true);
    }
  }, [query.data, primed]);

  const updateMut = useMutation({
    mutationFn: () => {
      const inv = query.data;
      const body: InvoiceUpdateBody = {
        issue_date: form.issue_date,
        due_date: form.due_date,
        notes_ru: form.notes_ru || null,
        // EFE-14: optimistic concurrency — отправляем updated_at (fallback на
        // created_at, BE сравнивает с тем же значением через assertVersionMatches).
        expected_updated_at: inv?.updated_at ?? inv?.created_at,
      };
      return updateInvoice(id, body);
    },
    onSuccess: () => {
      toast.push({ variant: 'success', title: m.invoice_toast_updated() });
      void qc.invalidateQueries({ queryKey: ['invoices', id] });
      void qc.invalidateQueries({ queryKey: ['invoices'] });
      void navigate({ to: '/invoices/$id', params: { id } });
    },
    onError: (err: unknown) =>
      toast.push({
        variant: 'error',
        title: m.invoice_toast_update_failed(),
        description: renderApiErrorMessage(err),
      }),
  });

  if (query.isLoading) return <div>…</div>;
  if (!query.data) return <div>Not found</div>;

  const inv = query.data;
  const readOnly = inv.state !== 'draft';

  return (
    <div>
      <PageHeader
        breadcrumbs={
          <Breadcrumbs
            items={[
              {
                label:
                  inv.kind === 'ar' ? m.phase2_invoices_ar_title() : m.phase2_invoices_ap_title(),
                to: inv.kind === 'ar' ? '/invoices/ar' : '/invoices/ap',
              },
              { label: inv.document_number, to: `/invoices/${id}` },
              { label: m.common_button_edit() },
            ]}
          />
        }
        title={m.invoice_edit_title()}
      />
      {/* access-control (F9) — контекст-плашка компании на edit-форме. */}
      <CompanyContextBanner />
      {readOnly && (
        <div className={[shared.inlineAlert, shared.inlineAlertWarn].join(' ')} role="status">
          {m.error_entity_not_editable({
            current_state: inv.state,
            editable_states: 'draft',
          })}
        </div>
      )}
      <div className={shared.formColumn480}>
        <div className={shared.formGrid2}>
          <DatePicker
            label={m.invoice_field_issue_date()}
            value={form.issue_date || null}
            onChange={(next) => setForm({ ...form, issue_date: next ?? '' })}
            disabled={readOnly}
          />
          <DatePicker
            label={m.invoice_field_due_date()}
            value={form.due_date || null}
            onChange={(next) => setForm({ ...form, due_date: next ?? '' })}
            disabled={readOnly}
          />
        </div>
        <Textarea
          label={m.invoice_field_notes()}
          value={form.notes_ru}
          onChange={(e) => setForm({ ...form, notes_ru: e.target.value })}
          disabled={readOnly}
          rows={3}
        />
      </div>
      <div className={shared.stickyFooter}>
        <Button
          type="button"
          variant="secondary"
          onClick={() => navigate({ to: '/invoices/$id', params: { id } })}
        >
          {m.common_button_cancel()}
        </Button>
        <Button
          variant="primary"
          onClick={() => updateMut.mutate()}
          loading={updateMut.isPending}
          disabled={readOnly}
        >
          {m.common_button_save()}
        </Button>
      </div>
    </div>
  );
}
